(ns common.opensearch.access
  (:require
   [common.attribute :as attribute]
   [common.features.plan :as plan]
   [common.opensearch.entity-indices :as c-entity-indices]
   [common.opensearch.read-gates :as read-gates]
   [common.opensearch.utils :as os-utils]
   [common.permissions.account-permissions :as account-permissions]
   [common.schema.assets :as assets]
   [common.schema.data-types :as data-types]
   [common.schema.registry :as registry]))
;; cos = your existing field-path -> OS-field helpers, should be cljc-safe

;; -----------------------------------------------------------------------------
;; 1) Logical field access (no OpenSearch details yet)
;; -----------------------------------------------------------------------------

(defn- gate-satisfied?
  "Whether this account satisfies an entity type's declared read gate.

  Lives here rather than in `common.opensearch.read-gates` to keep that
  namespace pure data: the backend never needs this - it builds
  `permissions.accounts` from the same declaration instead
  (`backend.search.permissions/derive-permissions-fn-maker`) - so the
  field-path layer is the only account-level evaluator there is.

  A gate that is not account-level (a `:gate/document` marker, an unknown
  facet, an empty map, or no declaration at all) is UNDECIDABLE here, because
  this layer has no document in hand, and returns false. That is the fail-closed
  default the whole mechanism rests on.

  `plan/available`'s 3-arity is safe on both sides: the frontend's
  `@session/tenant-plan` is a cursor into `@session/tenant` at
  `:tenant/product-plan`, so the tenant map carries the plan on the frontend
  too and no caller has to thread it separately."
  [{:keys [gate tenant role account-current-permissions]}]
  (let [{:gate/keys [all-users? modules features]} gate]
    (cond
      (not (read-gates/account-level-gate? gate))
      false

      (not (every? (fn [feature] (plan/available feature tenant role)) features))
      false

      all-users?
      true

      (seq modules)
      (every? (fn [module]
                (account-permissions/account-module-access? role account-current-permissions module))
              modules)

      :else
      false)))

(defn- flattened-ref-policy
  "The `(owning type, field)` relationship policy, or nil when none is declared.

  Keyed per hop rather than by the whole field-path from the document root, so
  a two-hop path composes by conjunction: each hop consults its own owning
  type's declaration, and a deeper path can never grant more than its prefix."
  [entity-type field-key]
  (get-in c-entity-indices/entity-type->index-config
          [entity-type :flattened-ref-policies field-key]))

(defn- target-recursion-allowed?
  "Whether the root's audience may read `target-entity-type`'s own flattened
  fields through this relationship.

  The default, with no policy declared, is the TARGET's own read gate - which
  is a no-op wherever the target is ungated (company, person, the asset types),
  and therefore only bites on the handful of relationships that actually cross
  a permission boundary."
  [{:keys [target-entity-type tenant role account-current-permissions]}]
  (gate-satisfied? {:gate (read-gates/read-gate target-entity-type)
                    :tenant tenant
                    :role role
                    :account-current-permissions account-current-permissions}))

(defn- accessible-logical-field-paths
  "Pure field-level access calculation.

  Expects:
  - entity-schemas: {entity-type {field-key field-schema, ...}}
    *This is already materialized data, no Datomic access here.*
  - entity-type: main entity type keyword, e.g. :entity.type/screening-profile
  - field-readable?-fn: (fn [entity-type field-key] boolean)
    Decides if a single field on an entity-type is readable for the current user.
    Backend and frontend build this from their own account-permission data.
  - associated-entity-field->entity-types (optional):
      {parent-field [entity-type ...]}
    For example:
      {:screening-profile/target [:entity.type/company]}
  - reverse-ref-fields (optional):
      {reverse-attribute {:entity-types [entity-type ...]}}
    the ROOT entity type's own reverse relationships (see
    common.opensearch.entity-indices, e.g. company's
    {:investor/_legal-entity {:entity-types [:entity.type/investor]}}) -
    mirrors backend.domain.entities.materialization.flatten's own
    reverse-ref-fields handling, so what's filterable/facetable here always
    agrees with what's actually materialized into :flat/:labels. Only applied
    when this call is the true root (context-path nil) - a reverse
    relationship is never a real field in entity-schema below (there's no
    Datomic attribute to declare it under), and only ever belongs to the
    entity type that declared it, not one reached mid-walk.

  ROOT/TARGET GATE RECONCILIATION. All three flattening mechanisms
  (:field/deep-indexing?, associated-entity-field->entity-types, and
  reverse-ref-fields) write a DIFFERENT entity type's field values into a
  document whose own ACL is the root type's, so the target type's read gate is
  simply lost unless it is re-applied here at query time. Field-level access
  (account-permissions/field-read-access?) cannot express it: that is decided
  from role and field authorization alone and never consults account modules.

  Each hop is therefore gated by the TARGET type's own declared read gate
  (common.opensearch.read-gates), unless the relationship declares otherwise
  via :flattened-ref-policies on the OWNING type's index config. The default
  fails closed and is a no-op wherever the target is ungated. See
  ai/project/release-audit-remediation/handoff.md for the design, including why
  a per-relationship policy is needed at all rather than just applying the
  target's gate everywhere.

  Returns a set of logical field paths:
  - root fields as keywords, e.g. :screening-profile/first-name
  - nested associated fields as vectors, e.g. [:screening-profile/target :company/name]
  - always includes :entity/type for the main entity."
  [{:keys [tenant tenant-schema account role account-current-permissions pipeline-id entity-type context-path
           associated-entity-field->entity-types reverse-ref-fields]
    :or {context-path nil}
    :as args}]
  (let [entity-schema (registry/get-schema tenant-schema entity-type)
        real-field-paths
        (->> entity-schema
             (filter (fn [[k _]]
                       (account-permissions/field-read-access? tenant tenant-schema account role account-current-permissions
                                                               k entity-type pipeline-id)))
             (mapcat (fn [[field-attribute field-meta]]
                       (let [field-path (if (vector? context-path)
                                          (conj context-path field-attribute)
                                          [field-attribute])]
                         (cond
                           (not (data-types/ref-type? (:field/data-type field-meta)))
                           [field-path]

                           :else
                           (let [associated-entity-types (or (get associated-entity-field->entity-types field-attribute)
                                                             (get associated-entity-field->entity-types field-path))
                                 ;; concrete-entity-types matches what
                                 ;; backend.domain.entities.materialization.flatten's
                                 ;; materialize-deep-indexed-ref-fields expands, so the
                                 ;; policy below is resolved against the same types whose
                                 ;; values actually get written. Without it an abstract
                                 ;; :entity.type/asset would be gated as one undeclared
                                 ;; type while four concrete subtypes were materialized.
                                 target-entity-types (assets/concrete-entity-types
                                                      (if (seq associated-entity-types)
                                                        associated-entity-types
                                                        (:field/entity-types field-meta)))
                                 recurse? (or (:field/deep-indexing? field-meta)
                                              (seq associated-entity-types))
                                   ;; Keep the base ref field path available so listing context can
                                   ;; preserve its original ref data-type (:data.type/ref-*) for
                                   ;; grouping/filter policies. Without this, only display-name/id
                                   ;; subpaths are exposed and ref fields disappear from Group by.
                                 ref-leaf-paths [field-path
                                                 (conj field-path :db/id)
                                                 (conj field-path :entity/type)
                                                 (conj field-path :common/display-name)]]
                             (if-not recurse?
                               ref-leaf-paths
                               (let [policy (flattened-ref-policy entity-type field-attribute)
                                     allowed-entity-types
                                     (filter (fn [et]
                                               (target-recursion-allowed?
                                                {:target-entity-type et
                                                 :tenant tenant
                                                 :role role
                                                 :account-current-permissions account-current-permissions}))
                                             target-entity-types)
                                     ;; Granted as LEAVES, never recursed into - the point is to
                                     ;; keep one named column working, not to reopen the hop.
                                     root-visible-paths (map (fn [target-field]
                                                               (conj field-path target-field))
                                                             (:root-visible-fields policy))]
                                 (if (seq allowed-entity-types)
                                   (concat [field-path]
                                           root-visible-paths
                                           (mapcat (fn [et]
                                                     (accessible-logical-field-paths (assoc args
                                                                                            :entity-type et
                                                                                            :context-path field-path)))
                                                   allowed-entity-types))
                                     ;; Fully denied: fall back to the non-recursing shape rather
                                     ;; than dropping the field. A forward ref is the ROOT's own
                                     ;; declared field - its existence and its label (the target's
                                     ;; display string, written by flatten's
                                     ;; assoc-materialized-field) are part of the root's data, and
                                     ;; the root's field authorization already governed them. Only
                                     ;; the target's OWN attributes are withheld.
                                   (concat ref-leaf-paths root-visible-paths))))))))))
        reverse-field-paths
        (when (nil? context-path)
          (mapcat (fn [[reverse-attribute {:keys [entity-types]}]]
                    (let [field-path [reverse-attribute]
                          allowed-entity-types
                          (filter (fn [et]
                                    (target-recursion-allowed?
                                     {:target-entity-type et
                                      :tenant tenant
                                      :role role
                                      :account-current-permissions account-current-permissions}))
                                  entity-types)]
                        ;; Denied means denied ENTIRELY here, unlike a forward ref, which
                        ;; falls back to its leaf paths. A reverse ref is not a field of
                        ;; the root at all - there is no Datomic attribute to declare it
                        ;; under, and it exists only because the SOURCE type points at
                        ;; this one. So its base path carries the source type's data too:
                        ;; labels.investor-_legal-entity_en is the display names of the
                        ;; investors pointing at this company, which is exactly what
                        ;; :module/fundraising gates. Keeping the base path on denial
                        ;; would leak the relationship's existence and the target's
                        ;; identity.
                      (when (seq allowed-entity-types)
                        (concat [field-path]
                                (mapcat (fn [et]
                                          (accessible-logical-field-paths (assoc args
                                                                                 :entity-type et
                                                                                 :context-path field-path)))
                                        allowed-entity-types)))))
                  reverse-ref-fields))]
    (concat real-field-paths reverse-field-paths)))

(defn last-field
  [field-path]
  (cond (keyword? field-path)
        field-path
        (sequential? field-path)
        (last-field (last field-path))
        :else
        (do (println "Unknown field-path " field-path)
            nil)))

(defn- extras-field-definitions
  [extras-fields]
  (mapv (fn [os-field]
          {:os-field os-field
           :short-label os-field
           :full-label os-field})
        extras-fields))

(defn accessible-dedicated-os-fields
  "High-level helper that combines:
  1) logical field access
  2) mapping to dedicated index OS fields.

  This function is still pure and cljc-safe. All Datomic/HTTP/etc. work
  must be done by the caller when building the arguments.

  Args:
  - {:entity-schemas                   {entity-type {field field-schema ...} ...}
     :entity-type                      :entity.type/...
     :tenant-schema                    <schema map>
     :field-readable?-fn               (fn [entity-type field-key] boolean)
     :associated-entity-field->entity-types (optional) {parent-field [entity-type ...]}
     :extras-fields                    (optional) #{\"flat.extra\" ...}}

  Returns:
  - #{\"flat.*\" \"labels.*\" ...} – the set of allowed OS field names for this
    user. Never original.*: that sub-document is display-only on dedicated
    indexes (see ai/opensearch-document-model.md)."
  [{:keys [_tenant tenant-schema _account _role _account-current-permissions _pipeline-id _entity-type extras-fields] :as args}]
  (let [logical (accessible-logical-field-paths args)
        ;; Labels must go through the tenant schema. `loc/tr` only ever sees the
        ;; static cross-entity schema, so every tenant custom field
        ;; (`cur-<tenant-id>-...`, and its `--sym` companion) missed and fell
        ;; back to the bare attribute name.
        label (fn [field-path]
                (attribute/attribute-label tenant-schema field-path))]
    (->> logical
         (mapv (fn [field-path]
                 {:field-path field-path
                  :os-field (os-utils/field-path->alias field-path)
                  :short-label (label (last-field field-path))
                  :full-label (label field-path)}))
         ;; Not a schema field - :common/display-name is computed, so the
         ;; schema-driven walk above cannot produce it.
         ;; flatten/materialize-document writes it into :flat explicitly, which
         ;; is what makes this os-field real rather than aspirational.
         (concat [{:field-path :common/display-name
                   :os-field "flat.common-display-name"
                   :short-label (label :common/display-name)
                   :full-label (label :common/display-name)}])
         (concat (extras-field-definitions extras-fields)))))
